/**
 * REVISÃO E APROVAÇÃO PLH — mecanismo central.
 *
 * Um único ciclo de revisão serve a todos os relatórios técnicos (LP, ME,
 * PM, B-Scan, Integridade…): o documento continua no seu módulo, com o seu
 * id e a sua revisão; aqui fica só o ciclo — quem enviou, para qual PLH,
 * o snapshot do que foi enviado, correções, parecer e aprovação. Aprovar a
 * análise NÃO é assinar o documento: a assinatura segue o fluxo de cada
 * módulo (Professional.signatureImageId), depois da aprovação.
 */

export type TechnicalDocumentType = "LP" | "ME" | "PM" | "BSCAN" | "INTEGRITY" | "AVR" | "USC";

/** Engenheiro + PLH do Projeto como estavam no envio (congelados na revisão). */
export interface ResponsibilitySnapshot {
  projectId: string;
  projectCode: string;
  projectName: string;
  clientId: string;
  engineerProfessionalId: string | null;
  engineerName: string;
  engineerFunctionTitle: string;
  engineerCredential: string;
  plhProfessionalId: string | null;
  plhName: string;
  plhFunctionTitle: string;
  plhCredential: string;
  frozenAt: string;
}

export const TECHNICAL_DOCUMENT_TYPE_LABELS: Record<TechnicalDocumentType, string> = {
  LP: "LP",
  ME: "ME",
  PM: "PM",
  BSCAN: "B-SCAN",
  INTEGRITY: "INTEGRIDADE",
  AVR: "AVR",
  USC: "US-CONV."
};

export type TechnicalReviewStatus = "AWAITING_PLH_REVIEW" | "PLH_REVIEWING" | "CORRECTION_REQUESTED" | "PLH_APPROVED";

/** Rótulos em português — o enum nunca vaza para a interface. */
export const TECHNICAL_REVIEW_STATUS_LABELS: Record<TechnicalReviewStatus, string> = {
  AWAITING_PLH_REVIEW: "Aguardando Análise do PLH",
  PLH_REVIEWING: "Em Análise do PLH",
  CORRECTION_REQUESTED: "Correção Solicitada pelo PLH",
  PLH_APPROVED: "Aprovado pelo PLH"
};

/** Estados em que o documento fica bloqueado para edição técnica. */
export const REVIEW_LOCKING_STATUSES: readonly TechnicalReviewStatus[] = ["AWAITING_PLH_REVIEW", "PLH_REVIEWING", "PLH_APPROVED"];

export function reviewLocksDocument(status: TechnicalReviewStatus | null | undefined): boolean {
  return !!status && REVIEW_LOCKING_STATUSES.includes(status);
}

export interface TechnicalReviewActor {
  userId: string;
  professionalId: string | null;
  name: string;
}

/** Identificação do documento como a fila mostra — congelada no envio. */
export interface TechnicalDocumentSummary {
  documentNumber: string;
  revision: string;
  clientId: string;
  client: string;
  project: string;
  unitArea: string;
  tag: string;
  osNumber: string;
  subOsNumber: string;
  elaboratedBy: string;
  /** Projeto do documento — fonte de verdade do Engenheiro e do PLH responsáveis. */
  projectId?: string | null;
  /** Inspetor responsável (Professional) — destino das correções por item. */
  inspectorProfessionalId?: string | null;
  /** Tipo de equipamento e data da inspeção/ensaio — cabeçalho macro do workspace. */
  equipmentType?: string;
  inspectionDate?: string;
  /** Rota da página de elaboração do documento ("/ndt/lp/<id>"). */
  route: string;
}

export interface TechnicalReviewHistoryEntry {
  at: string;
  by: string;
  action: "SUBMITTED" | "REVIEW_STARTED" | "CORRECTION_REQUESTED" | "CORRECTION_DONE" | "RESUBMITTED" | "APPROVED" | "SIGNED" | "FINALIZED";
  detail: string;
  fromStatus?: TechnicalReviewStatus | null;
  toStatus?: TechnicalReviewStatus;
  professionalId?: string | null;
}

export const TECHNICAL_REVIEW_HISTORY_LABELS: Record<TechnicalReviewHistoryEntry["action"], string> = {
  SUBMITTED: "Enviado ao PLH",
  REVIEW_STARTED: "Análise iniciada",
  CORRECTION_REQUESTED: "Correção solicitada",
  CORRECTION_DONE: "Correção executada",
  RESUBMITTED: "Reenviado ao PLH",
  APPROVED: "Aprovado pelo PLH",
  SIGNED: "Assinado",
  FINALIZED: "Relatório finalizado"
};

export interface TechnicalDocumentReview {
  id: string;
  documentType: TechnicalDocumentType;
  documentId: string;
  revision: string;
  summary: TechnicalDocumentSummary;
  /** Quantas vezes esta revisão foi enviada (1 = primeiro envio). */
  cycle: number;
  submittedBy: TechnicalReviewActor;
  submittedAt: string;
  submissionNote: string;
  assignedPlhProfessionalId: string;
  assignedPlhName: string;
  status: TechnicalReviewStatus;
  reviewStartedAt: string | null;
  reviewedAt: string | null;
  correctionReason: string;
  correctionSection: string;
  correctionRequestedAt: string | null;
  correctedAt: string | null;
  reviewComment: string;
  approvedAt: string | null;
  approvedBy: TechnicalReviewActor | null;
  /** O documento exatamente como foi enviado — é isto que o PLH analisa. */
  submissionSnapshot: unknown;
  /** Engenheiro/PLH do Projeto congelados no envio — mudanças futuras no Projeto não alteram esta revisão. */
  responsibility?: ResponsibilitySnapshot | null;
  /** FINALIZAR RELATÓRIO (≠ assinar): snapshot final, edição ordinária bloqueada. */
  finalizedAt?: string | null;
  finalizedBy?: TechnicalReviewActor | null;
  /** Congelado na aprovação: tipo, id, revisão, DocumentModel, PLH, parecer, data/hora. */
  approvalSnapshot: { documentType: TechnicalDocumentType; documentId: string; revision: string; model: unknown; plhProfessionalId: string; plhName: string; comment: string; approvedAt: string } | null;
  signedAt: string | null;
  history: TechnicalReviewHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

export const TECHNICAL_REVIEW_HISTORY_FINALIZED = "FINALIZED";

/** Ações do botão da página de elaboração para um status de revisão + permissão. */
export function submitActionFor(review: Pick<TechnicalDocumentReview, "status"> | null, documentStatus: string, hasPermission: boolean): { show: boolean; label: string } {
  if (!hasPermission || documentStatus === "SIGNED") return { show: false, label: "" };
  if (!review) return { show: true, label: "Enviar para o PLH" };
  if (review.status === "CORRECTION_REQUESTED") return { show: true, label: "Reenviar para o PLH" };
  return { show: false, label: "" };
}

export function normalizeTechnicalReview(review: TechnicalDocumentReview): TechnicalDocumentReview {
  const r = review as any;
  if (!Array.isArray(r.history)) r.history = [];
  if (typeof r.cycle !== "number") r.cycle = 1;
  for (const key of ["submissionNote", "correctionReason", "correctionSection", "reviewComment", "assignedPlhName"]) if (typeof r[key] !== "string") r[key] = "";
  for (const key of ["reviewStartedAt", "reviewedAt", "correctionRequestedAt", "correctedAt", "approvedAt", "signedAt"]) if (r[key] === undefined) r[key] = null;
  if (r.approvedBy === undefined) r.approvedBy = null;
  if (r.approvalSnapshot === undefined) r.approvalSnapshot = null;
  if (r.responsibility === undefined) r.responsibility = null;
  if (r.finalizedAt === undefined) r.finalizedAt = null;
  if (r.finalizedBy === undefined) r.finalizedBy = null;
  return review;
}
